import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Bell, Plus, PencilSimple, Trash, TestTube, Check, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const STATUS_OPTIONS = ["Vigente", "Adjudicado", "Desierto", "Cancelado", "Finalizado"];

const emptyForm = {
  name: "",
  keywords: "",
  categories: "",
  entities: "",
  statuses: [],
  amount_min: 0,
  amount_max: 0,
  is_active: true,
};

export default function AlertsPage() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [quota, setQuota] = useState(null);

  const fetchQuota = async () => {
    try {
      const res = await api.get("/alerts/quota");
      setQuota(res.data);
    } catch {}
  };

  const fetchAlerts = async () => {
    try {
      const res = await api.get("/alerts");
      setAlerts(res.data.alerts || []);
    } catch (e) {
      toast.error("Error al cargar alertas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAlerts(); fetchQuota(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (alert) => {
    setEditing(alert);
    setForm({
      name: alert.name,
      keywords: (alert.keywords || []).join(", "),
      categories: (alert.categories || []).join(", "),
      entities: (alert.entities || []).join(", "),
      statuses: alert.statuses || [],
      amount_min: alert.amount_min || 0,
      amount_max: alert.amount_max || 0,
      is_active: alert.is_active,
    });
    setDialogOpen(true);
  };

  const toList = (str) => (str || "").split(",").map((s) => s.trim()).filter(Boolean);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("El nombre de la alerta es requerido");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        keywords: toList(form.keywords),
        categories: toList(form.categories),
        entities: toList(form.entities),
        statuses: form.statuses,
        amount_min: parseFloat(form.amount_min) || 0,
        amount_max: parseFloat(form.amount_max) || 0,
        is_active: form.is_active,
      };
      if (editing) {
        await api.put(`/alerts/${editing.id}`, payload);
        toast.success("Alerta actualizada");
      } else {
        await api.post("/alerts", payload);
        toast.success("Alerta creada");
      }
      setDialogOpen(false);
      fetchAlerts();
      fetchQuota();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === "object" && detail?.code === "PLAN_LIMIT_REACHED") {
        toast.error("Límite alcanzado", {
          description: detail.message,
          action: { label: "Ver planes", onClick: () => window.location.href = "/precios" },
        });
      } else {
        toast.error(typeof detail === "string" ? detail : "Error al guardar");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta alerta? Las notificaciones existentes no se borrarán.")) return;
    try {
      await api.delete(`/alerts/${id}`);
      toast.success("Alerta eliminada");
      fetchAlerts();
      fetchQuota();
    } catch (e) {
      toast.error("Error al eliminar");
    }
  };

  const handleTest = async (id) => {
    try {
      const res = await api.post(`/alerts/${id}/test`);
      toast.success(`${res.data.matches} licitaciones coincidentes encontradas`, {
        description: res.data.matches > 0 ? `Ej: ${res.data.sample[0]?.title?.substring(0, 60)}...` : "Aún no hay coincidencias",
      });
    } catch (e) {
      toast.error("Error al probar alerta");
    }
  };

  const toggleStatus = (status) => {
    setForm((f) => ({
      ...f,
      statuses: f.statuses.includes(status)
        ? f.statuses.filter((s) => s !== status)
        : [...f.statuses, status],
    }));
  };

  return (
    <div className="space-y-6" data-testid="alerts-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading font-black text-3xl tracking-tight">Alertas</h1>
          <p className="text-sm text-muted-foreground font-mono mt-1">
            Define criterios para recibir notificaciones automáticas de nuevas licitaciones
          </p>
        </div>
        <div className="flex items-center gap-3">
          {quota && (
            <div className="text-right">
              <p className="text-[11px] font-mono uppercase text-muted-foreground">
                Plan {quota.plan}
              </p>
              <p className="text-sm font-mono font-bold">
                {quota.unlimited ? (
                  <span>{quota.active} alertas · Ilimitado</span>
                ) : (
                  <span className={quota.at_limit ? "text-[#E63946]" : ""}>
                    {quota.active} / {quota.limit} alertas
                  </span>
                )}
              </p>
            </div>
          )}
          <Button
            onClick={openCreate}
            disabled={quota?.at_limit}
            className="rounded-none bg-[#002FA7] hover:bg-[#002FA7]/90"
            data-testid="create-alert-btn"
            title={quota?.at_limit ? `Límite de Plan ${quota.plan} alcanzado` : ""}
          >
            <Plus size={16} className="mr-2" /> Nueva alerta
          </Button>
        </div>
      </div>

      {quota?.at_limit && (
        <div className="flex items-start gap-3 p-4 bg-amber-50 border-l-4 border-amber-500" data-testid="quota-warning">
          <Warning size={20} className="text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-900">Has alcanzado tu límite de {quota.limit} alertas del Plan {quota.plan}</p>
            <p className="text-xs text-amber-800 mt-1">
              Desactiva una alerta o{" "}
              <Link to="/precios" className="underline font-bold">actualiza tu plan</Link>{" "}
              para agregar más.
            </p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-muted-foreground">Cargando...</div>
      ) : alerts.length === 0 ? (
        <Card className="rounded-none border-dashed">
          <CardContent className="py-16 text-center">
            <Bell size={48} className="mx-auto text-muted-foreground mb-4" />
            <p className="font-medium">No tienes alertas configuradas</p>
            <p className="text-sm text-muted-foreground mt-2">
              Crea tu primera alerta para recibir notificaciones de licitaciones que te interesen
            </p>
            <Button onClick={openCreate} className="mt-6 rounded-none bg-[#002FA7] hover:bg-[#002FA7]/90" data-testid="empty-create-alert-btn">
              <Plus size={16} className="mr-2" /> Crear alerta
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {alerts.map((a) => (
            <Card key={a.id} className="rounded-none" data-testid={`alert-card-${a.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="font-heading text-lg">{a.name}</CardTitle>
                  <Badge className={`rounded-none font-mono text-[10px] ${a.is_active ? "bg-[#2A9D8F] text-white" : "bg-secondary text-foreground"}`}>
                    {a.is_active ? "ACTIVA" : "INACTIVA"}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3 pt-0">
                {a.keywords?.length > 0 && (
                  <div className="text-xs">
                    <span className="font-mono text-muted-foreground">Palabras clave: </span>
                    <span className="font-medium">{a.keywords.join(", ")}</span>
                  </div>
                )}
                {a.categories?.length > 0 && (
                  <div className="text-xs">
                    <span className="font-mono text-muted-foreground">Categorías: </span>
                    <span className="font-medium">{a.categories.join(", ")}</span>
                  </div>
                )}
                {a.entities?.length > 0 && (
                  <div className="text-xs">
                    <span className="font-mono text-muted-foreground">Entidades: </span>
                    <span className="font-medium">{a.entities.join(", ")}</span>
                  </div>
                )}
                {(a.amount_min > 0 || a.amount_max > 0) && (
                  <div className="text-xs">
                    <span className="font-mono text-muted-foreground">Monto: </span>
                    <span className="font-medium">
                      {a.amount_min > 0 ? `Q${a.amount_min.toLocaleString()}` : "—"} a {a.amount_max > 0 ? `Q${a.amount_max.toLocaleString()}` : "∞"}
                    </span>
                  </div>
                )}
                {a.statuses?.length > 0 && (
                  <div className="text-xs">
                    <span className="font-mono text-muted-foreground">Estados: </span>
                    <span className="font-medium">{a.statuses.join(", ")}</span>
                  </div>
                )}
                <div className="flex items-center justify-between pt-2 border-t border-border/40">
                  <span className="text-[11px] font-mono text-muted-foreground">
                    {a.matches_count || 0} coincidencias totales
                  </span>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => handleTest(a.id)} className="rounded-none h-8" data-testid={`test-alert-${a.id}`}>
                      <TestTube size={14} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(a)} className="rounded-none h-8" data-testid={`edit-alert-${a.id}`}>
                      <PencilSimple size={14} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(a.id)} className="rounded-none h-8 hover:text-[#E63946]" data-testid={`delete-alert-${a.id}`}>
                      <Trash size={14} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editing ? "Editar alerta" : "Nueva alerta"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-mono uppercase">Nombre</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ej: Licitaciones de tecnología"
                className="rounded-none mt-1"
                data-testid="alert-name-input"
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Palabras clave (separadas por coma)</Label>
              <Input
                value={form.keywords}
                onChange={(e) => setForm({ ...form, keywords: e.target.value })}
                placeholder="computadora, laptop, servidor"
                className="rounded-none mt-1"
                data-testid="alert-keywords-input"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-mono uppercase">Categorías</Label>
                <Input
                  value={form.categories}
                  onChange={(e) => setForm({ ...form, categories: e.target.value })}
                  placeholder="Computación y telecomunicaciones"
                  className="rounded-none mt-1"
                  data-testid="alert-categories-input"
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Entidades</Label>
                <Input
                  value={form.entities}
                  onChange={(e) => setForm({ ...form, entities: e.target.value })}
                  placeholder="Ministerio de Salud, MINFIN"
                  className="rounded-none mt-1"
                  data-testid="alert-entities-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-mono uppercase">Monto mínimo (GTQ)</Label>
                <Input
                  type="number"
                  value={form.amount_min}
                  onChange={(e) => setForm({ ...form, amount_min: e.target.value })}
                  className="rounded-none mt-1"
                  data-testid="alert-amount-min-input"
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Monto máximo (GTQ)</Label>
                <Input
                  type="number"
                  value={form.amount_max}
                  onChange={(e) => setForm({ ...form, amount_max: e.target.value })}
                  className="rounded-none mt-1"
                  data-testid="alert-amount-max-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase mb-2 block">Estados</Label>
              <div className="flex flex-wrap gap-2">
                {STATUS_OPTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => toggleStatus(s)}
                    className={`text-xs font-mono px-3 py-1.5 border transition-colors ${
                      form.statuses.includes(s)
                        ? "bg-[#002FA7] text-white border-[#002FA7]"
                        : "bg-background text-foreground border-border hover:bg-secondary"
                    }`}
                    data-testid={`alert-status-${s}`}
                  >
                    {form.statuses.includes(s) && <Check size={12} className="inline mr-1" />}
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-border/40">
              <Label className="text-sm">Alerta activa</Label>
              <Switch
                checked={form.is_active}
                onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                data-testid="alert-active-switch"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none" data-testid="alert-cancel-btn">
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving} className="rounded-none bg-[#002FA7] hover:bg-[#002FA7]/90" data-testid="alert-save-btn">
              {saving ? "Guardando..." : editing ? "Actualizar" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
